import { CSSProperties } from 'react'
import styled, { keyframes } from 'styled-components'

/**
 * Animated HAUP mark used for page / route loading.
 *
 * The four blades of the logo light up in a diagonal wave (top-left -> bottom-right)
 * while the whole mark gently breathes. Honours `prefers-reduced-motion` by falling
 * back to a single calm pulse.
 */

const BRAND_BLUE = '#0046A0'

const wave = keyframes`
  0%, 62%, 100% {
    opacity: 0.16;
  }
  28% {
    opacity: 1;
  }
`

const breathe = keyframes`
  0%, 100% {
    transform: scale(0.94);
  }
  50% {
    transform: scale(1.06);
  }
`

const glowPulse = keyframes`
  0%, 100% {
    opacity: 0.25;
    transform: translate(-50%, -50%) scale(0.85);
  }
  50% {
    opacity: 0.55;
    transform: translate(-50%, -50%) scale(1.15);
  }
`

const trackSlide = keyframes`
  0% {
    transform: translateX(-110%);
  }
  100% {
    transform: translateX(360%);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled.div<{ $fixed?: boolean; $backdrop: string }>`
  position: ${(p) => (p.$fixed ? 'fixed' : 'absolute')};
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  background: ${(p) => p.$backdrop};
  backdrop-filter: blur(2px);
  animation: ${fadeIn} 0.25s ease-out both;
  cursor: wait;
`

const MarkStage = styled.div<{ $size: number; $color: string; $glow: string }>`
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  color: ${(p) => p.$color};
  display: grid;
  place-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 150%;
    height: 150%;
    border-radius: 50%;
    background: radial-gradient(circle, ${(p) => p.$glow} 0%, transparent 68%);
    filter: blur(6px);
    animation: ${glowPulse} 1.7s ease-in-out infinite;
  }
`

const Mark = styled.svg`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
  animation: ${breathe} 1.7s ease-in-out infinite;

  path {
    fill: currentColor;
    animation: ${wave} 1.7s ease-in-out infinite;
    will-change: opacity;
  }

  path.hb-tl {
    animation-delay: 0s;
  }
  path.hb-tr,
  path.hb-bl {
    animation-delay: 0.16s;
  }
  path.hb-br {
    animation-delay: 0.32s;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    path {
      animation-delay: 0s !important;
    }
  }
`

const Track = styled.div<{ $color: string }>`
  position: relative;
  width: 132px;
  height: 3px;
  border-radius: 999px;
  overflow: hidden;
  background: currentColor;
  color: ${(p) => p.$color};
  opacity: 0.9;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 45%;
    border-radius: 999px;
    background: ${(p) => p.$color};
    filter: brightness(1.9);
    animation: ${trackSlide} 1.15s cubic-bezier(0.65, 0, 0.35, 1) infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation-duration: 2s;
    }
  }
`

const Tip = styled.span<{ $color: string }>`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: ${(p) => p.$color};
  opacity: 0.75;
`

export interface HaupLogoLoaderProps {
  /** `dark` = light mark on a dark/brand backdrop, `light` = brand-blue mark on a light backdrop. */
  variant?: 'light' | 'dark'
  /** Diameter of the mark in px. */
  size?: number
  /** Position `fixed` (full viewport) vs `absolute` (fills nearest positioned parent). */
  fixed?: boolean
  /** Override the backdrop fill. */
  backdrop?: string
  /** Show the sliding progress track beneath the mark. */
  showTrack?: boolean
  /** Optional caption under the mark. */
  tip?: string
  style?: CSSProperties
  className?: string
}

export const HaupLogoLoader = ({
  variant = 'light',
  size = 76,
  fixed = true,
  backdrop,
  showTrack = false,
  tip,
  style,
  className,
}: HaupLogoLoaderProps): JSX.Element => {
  const isDark = variant === 'dark'

  const markColor = isDark ? '#ffffff' : BRAND_BLUE
  const glowColor = isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 70, 160, 0.28)'
  const tipColor = isDark ? 'rgba(255, 255, 255, 0.85)' : '#445064'
  const resolvedBackdrop =
    backdrop ??
    (isDark
      ? `radial-gradient(circle at 50% 38%, #0b56bd 0%, ${BRAND_BLUE} 52%, #00306e 100%)`
      : 'rgba(255, 255, 255, 0.82)')

  return (
    <Root $fixed={fixed} $backdrop={resolvedBackdrop} style={style} className={className}>
      <MarkStage $size={size} $color={markColor} $glow={glowColor}>
        <Mark viewBox="0 0 451.81 451.79" role="img" aria-label="HAUP loading">
          <path
            className="hb-br"
            d="M281.47,451.79a46,46,0,0,1-32.55-78.6L373.19,248.93A46,46,0,0,1,438.31,314L314,438.3A45.74,45.74,0,0,1,281.47,451.79Z"
          />
          <path
            className="hb-bl"
            d="M46,451.79a46,46,0,0,1-32.54-78.6L137.77,248.91A46,46,0,0,1,202.87,314L78.61,438.3A45.74,45.74,0,0,1,46,451.79Z"
          />
          <path
            className="hb-tr"
            d="M281.47,216.39a46.09,46.09,0,0,1-32.55-78.63L373.19,13.47a46.05,46.05,0,0,1,65.12,65.14L314,202.85A45.92,45.92,0,0,1,281.47,216.39Z"
          />
          <path
            className="hb-tl"
            d="M46,216.39A46.09,46.09,0,0,1,13.5,137.76L137.77,13.47a46,46,0,0,1,65.1,65.14L78.61,202.85A45.92,45.92,0,0,1,46,216.39Z"
          />
        </Mark>
      </MarkStage>

      {showTrack && <Track $color={markColor} />}
      {tip && <Tip $color={tipColor}>{tip}</Tip>}
    </Root>
  )
}

export default HaupLogoLoader
